/** What a press on one of the console's stone buttons means.
 *
 * Split out of `BattleScreen.tsx` for one reason: the mapping used to be a
 * chain of `if`s ending in
 *
 *     onCommand({ type: action === "retreat" ? "BATTLE_RETREAT" : "BATTLE_SURRENDER", side })
 *
 * which is a two-way ternary standing at the end of what is now a five-way set
 * of actions. Every action without a branch of its own above it reached that
 * line and **surrendered the army**, so enabling the spellbook button before
 * giving it a branch would have lost the battle on the first press. A ternary
 * cannot be tested from a React tree without a canvas; a pure function can, and
 * `apps/web/test/battle-console-command.spec.ts` walks the whole of
 * `BATTLE_ACTIONS` through it to prove that nothing but "surrender" surrenders.
 *
 * Nothing here decides legality. `enumerateLegalBattleCommands` does that, and
 * the console only offers the buttons the app put in `UiState.enabled` (I10);
 * this returns the command a press *means*, and the kernel still refuses it if
 * it is not allowed.
 */

import type { BattleCommand } from "@game/core";
import type { BattleAction } from "@game/render";

/** Who is pressing, and what the board says about them. */
export interface ConsoleContext {
  /** Slot of the active stack, or -1 when nothing is acting. */
  readonly slot: number;
  /** True when the active stack is one the viewer may order. */
  readonly mine: boolean;
  /** The side the viewer controls, or null when spectating. */
  readonly side: 0 | 1 | null;
  /**
   * True while the viewer's own tactics placement is active. Turn commands
   * are meaningless then - the kernel accepts placements and the one-shot
   * that ends the phase, and nothing else - so every stone keeps its paint
   * but only the DONE key still means anything.
   */
  readonly tactics?: boolean;
}

export type ConsoleIntent =
  /** Issue this to the kernel. */
  | { readonly kind: "command"; readonly command: BattleCommand }
  /** Open or close the book. It is a screen, not a kernel command. */
  | { readonly kind: "spellbook" }
  /**
   * Arm the hero's blow and wait for a target, the way an armed spell waits.
   *
   * It cannot be a `command` because the command needs an enemy stack and a
   * console button carries no hex. The screen puts it in the same aiming state
   * a targeted cast uses, so right-click disarms it and opening the book
   * disarms it, which is what a player expects of anything that is "armed".
   */
  | { readonly kind: "aimStrike" }
  /** The press means nothing right now. */
  | { readonly kind: "none" };

const NONE: ConsoleIntent = { kind: "none" };

export function consoleIntent(action: BattleAction, ctx: ConsoleContext): ConsoleIntent {
  // During the viewer's own tactics placement the kernel takes placements on
  // the board (`tacticsClick` below) and the one-shot that ends the phase.
  // The console's DONE key is the defend stone - the rightmost of the pair
  // that ends a turn anywhere else - and every other stone is dead, because
  // whatever it issued the kernel would refuse.
  if (ctx.tactics === true) {
    return action === "defend" && ctx.side !== null
      ? { kind: "command", command: tacticsDone(ctx.side) }
      : NONE;
  }
  switch (action) {
    case "spellbook":
      return { kind: "spellbook" };
    case "wait":
      return ctx.mine && ctx.slot >= 0
        ? { kind: "command", command: { type: "BATTLE_WAIT", stack: ctx.slot } }
        : NONE;
    case "defend":
      return ctx.mine && ctx.slot >= 0
        ? { kind: "command", command: { type: "BATTLE_DEFEND", stack: ctx.slot } }
        : NONE;
    case "retreat":
      return ctx.side === null
        ? NONE
        : { kind: "command", command: { type: "BATTLE_RETREAT", side: ctx.side } };
    case "surrender":
      return ctx.side === null
        ? NONE
        : { kind: "command", command: { type: "BATTLE_SURRENDER", side: ctx.side } };
    case "strike":
      // The hero's blow needs a target, so the press arms rather than issues.
      return ctx.side === null || !ctx.mine ? NONE : { kind: "aimStrike" };
    case "racial":
      // The racial is spent on the stack whose turn it is, which is the only
      // stack the kernel will accept - so the button carries no choice at all.
      return ctx.mine && ctx.slot >= 0 && ctx.side !== null
        ? { kind: "command", command: { type: "BATTLE_RACIAL", side: ctx.side, stack: ctx.slot } }
        : NONE;
    case "activate":
      return ctx.mine && ctx.slot >= 0
        ? { kind: "command", command: { type: "BATTLE_ACTIVATE", stack: ctx.slot } }
        : NONE;
    // No default that guesses. A sixth action added to `BATTLE_ACTIONS` and not
    // named here is a dead button, which is the only safe direction: the switch
    // is exhaustive over the union, so the compiler fails on the addition
    // rather than the player losing an army to it.
  }
}

// ------------------------------------------------------------- tactics phase

/**
 * What a click on the board means during the tactics phase.
 *
 * The kernel enumerates placements (`BATTLE_TACTICS_MOVE`) and the one-shot
 * that ends the phase (`BATTLE_TACTICS_DONE`). This file only says which of
 * those a hex press is: click one of your stacks to pick it, click a hex it
 * may occupy to place it. Nothing here decides the band - `legal` already
 * filtered that.
 */
export type TacticsClick =
  | { readonly kind: "select"; readonly stack: number }
  | { readonly kind: "command"; readonly command: BattleCommand }
  | { readonly kind: "none" };

const TACTICS_NONE: TacticsClick = { kind: "none" };

function canPlace(legal: readonly BattleCommand[], stack: number): boolean {
  for (const c of legal) {
    if (c.type === "BATTLE_TACTICS_MOVE" && c.stack === stack) return true;
  }
  return false;
}

export function tacticsClick(
  hex: number,
  occupant: number | null,
  selected: number,
  legal: readonly BattleCommand[],
): TacticsClick {
  if (occupant !== null && canPlace(legal, occupant)) {
    return { kind: "select", stack: occupant };
  }
  if (selected < 0) return TACTICS_NONE;
  for (const c of legal) {
    if (c.type === "BATTLE_TACTICS_MOVE" && c.stack === selected && c.hex === hex) {
      return { kind: "command", command: c };
    }
  }
  return TACTICS_NONE;
}

/** The button that ends the phase. The side is the viewer's, which is also
 *  the only side the kernel will accept a DONE from. */
export function tacticsDone(side: 0 | 1): BattleCommand {
  return { type: "BATTLE_TACTICS_DONE", side };
}
